import React, { useRef, useState } from "react";
import { MagnifyingGlass, CircleNotch, MapPin, X } from "@phosphor-icons/react";

/**
 * AddressSearch — lightweight, anonymized geocoder using OSM Nominatim.
 *
 * Nominatim is free, requires no API key, rate-limited to ~1 req/s per IP.
 * We debounce typing by 350ms and limit to DACH (country code bias).
 * Results never leave the browser — no personal data logged on our server.
 *
 * Props:
 *   onPick(lon, lat, label) — callback when a result is clicked
 *   placeholder              — input placeholder
 *   testid                   — optional data-testid override
 */
export default function AddressSearch({ onPick, placeholder = "Adresse / Ort / PLZ suchen…", testid = "address-search" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const debounce = useRef(null);

  const doSearch = async (value) => {
    if (value.length < 3) { setResults([]); return; }
    setLoading(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6&countrycodes=de,at,ch&accept-language=de&q=${encodeURIComponent(value)}`;
      const r = await fetch(url, { headers: { "Accept": "application/json" } });
      const data = await r.json();
      setResults(Array.isArray(data) ? data : []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const onChange = (e) => {
    const v = e.target.value;
    setQ(v);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => doSearch(v), 350);
  };

  const pick = (r) => {
    onPick?.(parseFloat(r.lon), parseFloat(r.lat), r.display_name);
    setQ(r.display_name.split(",")[0]);
    setResults([]);
  };

  const clear = () => { setQ(""); setResults([]); };

  return (
    <div className="relative" data-testid={testid}>
      <div className="flex items-center gap-2 bg-[#121212] border border-white/10 rounded-sm px-3 py-2 focus-within:border-[#00FA9A]/40">
        {loading ? <CircleNotch size={14} className="animate-spin text-zinc-500" /> : <MagnifyingGlass size={14} className="text-zinc-500" />}
        <input
          value={q}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 200)}
          placeholder={placeholder}
          data-testid={`${testid}-input`}
          className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-zinc-500"
        />
        {q && (
          <button onClick={clear} className="text-zinc-500 hover:text-white" aria-label="Leeren" data-testid={`${testid}-clear`}>
            <X size={12} />
          </button>
        )}
      </div>
      {focused && results.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full mt-1 bg-[#121212] border border-white/10 rounded-sm shadow-lg max-h-72 overflow-y-auto z-[1001]"
          data-testid={`${testid}-results`}
        >
          {results.map((r) => (
            <button
              key={r.place_id}
              onMouseDown={() => pick(r)}
              data-testid={`${testid}-result-${r.place_id}`}
              className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/10 last:border-b-0 flex items-start gap-2"
            >
              <MapPin size={12} className="shrink-0 mt-0.5 text-[#00FA9A]" />
              <div className="min-w-0">
                <div className="text-sm truncate">{r.display_name.split(",")[0]}</div>
                <div className="text-[10px] font-mono text-zinc-500 truncate">
                  {r.display_name.split(",").slice(1, 4).join(", ")}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      {focused && q.length >= 3 && !loading && results.length === 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-[#121212] border border-white/10 rounded-sm p-3 text-xs text-zinc-500 z-[1001]">
          Keine Treffer — versuche „Stadt + PLZ" oder „Straße, Ort".
        </div>
      )}
    </div>
  );
}
